import { useMutation } from "@tanstack/react-query";
import { KeyRound, ShieldCheck } from "lucide-react";
import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "./api.js";
import type { PlatformBranding } from "./adminOperations.js";

export function ResetPasswordPage({ branding }: { branding: PlatformBranding }) {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const reset = useMutation({
    mutationFn: () =>
      api("/api/v1/auth/password-reset/confirm", {
        method: "POST",
        body: JSON.stringify({ token, newPassword: password }),
      }),
  });
  function submit(event: FormEvent): void {
    event.preventDefault();
    if (password === confirmation) reset.mutate();
  }
  return (
    <main className="player-pairing">
      <section className="pairing-card password-reset-card">
        <span className="pairing-icon">
          <KeyRound size={32} />
        </span>
        <span className="eyebrow">{branding.name} account security</span>
        <h1>Set a new client password</h1>
        {!token ? (
          <>
            <div className="form-error">This reset link is missing its secure token.</div>
            <Link className="button button--secondary button--wide" to="/login">
              Return to sign in
            </Link>
          </>
        ) : reset.isSuccess ? (
          <>
            <div className="notice notice--good">
              <ShieldCheck size={18} />
              <span>Password changed and all prior sessions were invalidated.</span>
            </div>
            <Link className="button button--primary button--wide" to="/login?passwordChanged=1">
              Sign in
            </Link>
          </>
        ) : (
          <form className="form-stack" onSubmit={submit}>
            <label>
              <span>New password</span>
              <input
                type="password"
                autoComplete="new-password"
                minLength={12}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                autoFocus
              />
            </label>
            <label>
              <span>Confirm password</span>
              <input
                type="password"
                autoComplete="new-password"
                value={confirmation}
                onChange={(event) => setConfirmation(event.target.value)}
                required
              />
            </label>
            {password !== confirmation ? (
              <div className="form-error">The passwords do not match.</div>
            ) : null}
            {reset.error ? (
              <div className="form-error" role="alert">
                {reset.error instanceof Error ? reset.error.message : "Reset failed."}
              </div>
            ) : null}
            <button
              className="button button--primary button--wide"
              type="submit"
              disabled={reset.isPending || password !== confirmation || password.length < 12}
            >
              {reset.isPending ? "Changing password…" : "Change password"}
            </button>
          </form>
        )}
      </section>
    </main>
  );
}
